import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import {
  SELECT_PROP_TYPES,
  SELECT_AUTOCOMPLETE_PROP_TYPES,
  SELECT_MENU_PROP_TYPES,
  SELECT_MULTIPLE_PROP_TYPES,
} from './constants';

function SelectAutocomplete(props) {
  const {
    disabled,
    error,
    events = {},
    label,
    name,
    helperText,
    size,
    value,
    onBlur,
    onChange,
    loading = false,
  } = props;
  let { options = [] } = props;

  if (typeof options === 'function') {
    options = options();
  }

  return (
    <Autocomplete
      disabled={disabled || loading}
      loading={loading}
      onChange={(event, value) => {
        const target = {
          name,
          value: value?.id,
        };

        onChange({
          target, 
        });

        if (events.onChange) {
          events.onChange(target);
        }
      }}
      onBlur={onBlur}
      options={options}
      getOptionLabel={({ name }) => name}
      groupBy={({ groupBy }) => groupBy}
      defaultValue={options.find((_) => String(_.id) === String(value))}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          fullWidth
          InputProps={{
            ...params.InputProps,
            style: {
              borderRadius: '17px',
              height: '50px',
              border: '#CED8E3',
            },
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          size={size}
          error={error}
          helperText={helperText}
          label={label}
          variant="outlined"
        />
      )}
    />
  );
}

function SelectMenu(props) {
  const {
    disabled,
    emptyElement,
    error,
    events = {},
    group,
    helperText,
    label,
    name,
    onBlur,
    onChange,
    size,
    value,
    loading = false,
  } = props;
  let { options = [] } = props;

  if (typeof options === 'function') {
    options = options();
  }

  const groups = group
    ? [...new Set(options.map(({ groupBy }) => groupBy))]
    : [];

  return (
    <Box position="relative">
      <TextField
        autoComplete="off"
        disabled={disabled || loading}
        error={error}
        fullWidth
        helperText={helperText}
        InputProps={{
          style: {
            borderRadius: '17px',
            height: '50px',
            border: '#CED8E3',
          },
        }}
        InputLabelProps={{
          shrink: true,
        }}
        label={label}
        name={name}
        select
        SelectProps={{
          native: true,
        }}
        size={size}
        variant="outlined"
        value={value}
        onBlur={onBlur}
        onChange={(e) => {
          onChange(e);
          if (events.onChange) {
            events.onChange(e?.target);
          }
        }}
      >
        {emptyElement && <option>{emptyElement}</option>}
        {group
          ? groups.map((item, index) => {
            const opt = options.filter((_) => _.groupBy === item);
            return opt.length ? (
              <optgroup key={index} label={item} style={{
                fontStyle: 'normal', 
              }}>
                {opt.map(({ id, name }, key) => (
                  <option value={id} key={key}>
                    {name}
                  </option>
                ))}
              </optgroup>
            ) : null;
          })
          : options.map(({ id, name }, key) => (
            <option value={id} key={key}>
              {name}
            </option>
          ))}
      </TextField>
      {loading && (
        <CircularProgress
          size={20}
          style={{
            position: 'absolute',
            top: '50%',
            right: '10px',
            marginTop: '-10px',
          }}
        />
      )}
    </Box>
  );
}

function SelectMultiple(props) {
  const {
    disabled,
    error,
    events = {},
    helperText,
    label,
    name,
    onChange,
    size,
    loading = false,
  } = props;
  let { options = [], value = [] } = props;

  if (!Array.isArray(value)) {
    value = [];
  }

  if (typeof options === 'function') {
    options = options();
  }

  return (
    <FormControl fullWidth size={size} variant="outlined" style={{
      position: 'relative', 
    }}>
      <InputLabel children={label} />
      <Select
        disabled={disabled || loading}
        error={error}
        input={<OutlinedInput label={label} />}
        multiple
        name={name}
        onChange={(e) => {
          onChange(e);
          if (events.onChange) {
            events.onChange(e?.target);
          }
        }}
        renderValue={(selected) =>
          `${selected.length} seleccionado${selected.length !== 1 ? 's' : ''}`
        }
        value={value}
        variant="outlined"
      >
        {options.map((option, index) => (
          <MenuItem key={index} value={option.id}>
            {!disabled && <Checkbox checked={value.includes(option.id)} />}
            <ListItemText primary={option.name} />
          </MenuItem>
        ))}
      </Select>
      <FormHelperText children={helperText} error />
      {loading && (
        <CircularProgress
          size={20}
          style={{
            position: 'absolute',
            top: '50%',
            right: '10px',
            marginTop: '-10px',
          }}
        />
      )}
    </FormControl>
  );
}

function SelectControl(props) {
  const {
    disabled,
    label,
    name,
    settings: {
      autocomplete = false,
      emptyElement,
      group = false,
      multiple = false,
      options = [],
    },
    events = {},
    size,
    value,
    error,
    helperText,
    onBlur,
    onChange,
    loading = false,
  } = props;

  const Control = multiple
    ? SelectMultiple
    : autocomplete
      ? SelectAutocomplete
      : SelectMenu;

  return (
    <Box sx={{
      mb: 2, 
    }}>
      <Typography style={{
        fontWeight: 'bold', 
      }} variant="h6" gutterBottom>
        {label}
      </Typography>
      <Control
        disabled={disabled}
        emptyElement={emptyElement}
        error={error}
        events={events}
        group={group}
        helperText={helperText}
        multiple={multiple}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        options={options}
        size={size}
        value={value}
        loading={loading}
      />
    </Box>
  );
}

SelectAutocomplete.propTypes = SELECT_AUTOCOMPLETE_PROP_TYPES;
SelectMenu.propTypes = SELECT_MENU_PROP_TYPES;
SelectMultiple.propTypes = SELECT_MULTIPLE_PROP_TYPES;
SelectControl.propTypes = SELECT_PROP_TYPES;

SelectControl.defaultProps = {
  disabled: false,
  settings: {
    autocomplete: false,
    group: false,
    options: [],
  },
  size: 'small',
  onChange: () => {},
  onBlur: () => {},
};

export default SelectControl;
